/**
 * Meal Plan Users — vocabulary and mappers.
 *
 * This module is pure. It turns what the API returns into what the screen shows
 * and nothing else: no fetching (see mealPlanUsersApi.js), no local mutation of
 * assignment state (the server owns that now).
 *
 * Two vocabularies meet here:
 *   - the platform speaks in RESTRICTION  (restrict / unrestrict)
 *   - the product speaks in ASSIGNMENT    (assign / unassign, six lifecycle states)
 * Everything below translates between them in one direction only, so the screen
 * never has to know which endpoint it is really talking to.
 */

import moment from 'moment';

/* ---------------- Dates ----------------
   The wallet APIs carry every date as epoch SECONDS. */
export const fromEpoch = seconds =>
  seconds == null ? null : moment.unix(Number(seconds));

export const fmtDate = value => {
  if (value == null || value === '') return '';
  const m = typeof value === 'number' ? fromEpoch(value) : moment(value, 'YYYY-MM-DD');
  return m && m.isValid() ? m.format('D MMM YY') : '';
};

export const fmtNumber = n => (n == null ? '' : Number(n).toLocaleString('en-IN'));

/* ---------------- Plans and meal types ----------------

   The screen selects a MEAL TYPE, not an offer. That is the endpoint's own
   shape: /roster, /restrict and /unrestrict are all keyed by `mealType`, so two
   tabs for two offers of the same type would answer with one identical roster.
   /plans still lists offers ("VegLunchDev", "NonVegMeal"), each naming its meal
   type, so the offers are grouped into the tabs the API can actually address
   and their names are kept for display underneath.

   MEAL_TYPES is the endpoint's own closed enum, verified against it: anything
   outside these three exact strings comes back as
     "mealType[0]" must be one of [Breakfast, Lunch, Dinner]
   so the values are the labels, and the casing is part of the contract. */

export const MEAL_TYPES = ['Breakfast', 'Lunch', 'Dinner'];

/* The API's own mealType is authoritative, but the enum is closed, so a value
   outside it is not something the endpoint could be asked about. Matching is
   case-insensitive on the way in and canonical on the way out — the request
   carries the exact string the schema accepts, whatever case /plans used. */
export const normalizeMealType = value => {
  const upper = String(value || '').trim().toUpperCase();
  if (!upper) return null;
  return MEAL_TYPES.find(type => upper.includes(type.toUpperCase())) || null;
};

/* Fallback only: before /plans carried mealType, the offer name was the only
   place the meal type was declared. Kept so an offer missing the field still
   lands in the right tab. */
export const mealTypeOf = name => normalizeMealType(name);

/**
 * An offer as the screen needs it. `pointsPerUser` and the validity window are
 * the only commercial terms the API returns, and MPC-2 keeps them off this
 * screen — they are carried for the confirmation copy, not for display on the
 * selector.
 */
export const normalizePlan = plan => ({
  id: plan.walletOfferId,
  name: plan.walletOfferName,
  mealType: normalizeMealType(plan.mealType) || mealTypeOf(plan.walletOfferName),
  pointsName: plan.walletPointsName,
  pointsPerUser: plan.pointsPerUser ?? null,
  validFrom: plan.activeFrom ?? null,
  validTo: plan.activeTo ?? null,
  store: plan.storeInfo || null,
  syncStatus: plan.syncStatus || null,
});

/* The widest window the offers behind one tab span. A single offer without an
   end date leaves the whole tab open-ended — the alternative would announce an
   expiry the company does not actually have. */
const spanOf = (plans, field, pick) => {
  const values = plans.map(p => p[field]);
  if (values.some(v => v == null)) return null;
  return values.reduce((acc, v) => pick(acc, Number(v)), Number(values[0]));
};

/**
 * The selector's tabs: one per meal type the company has an offer for, in the
 * enum's own Breakfast / Lunch / Dinner order. `id` is the exact string sent to
 * the API, so the tab and the request cannot drift apart.
 */
export const mealTypeTabs = plans => {
  const groups = new Map();

  plans.forEach(plan => {
    if (!plan.mealType) return; /* see unmappedOffers — named, not selectable */
    if (!groups.has(plan.mealType)) {
      groups.set(plan.mealType, {
        id: plan.mealType,
        mealType: plan.mealType,
        name: plan.mealType,
        plans: [],
      });
    }
    groups.get(plan.mealType).plans.push(plan);
  });

  return MEAL_TYPES.filter(type => groups.has(type)).map(type => {
    const group = groups.get(type);
    return {
      ...group,
      offerNames: group.plans.map(p => p.name).filter(Boolean),
      pointsName: group.plans[0]?.pointsName || null,
      validFrom: spanOf(group.plans, 'validFrom', Math.min),
      validTo: spanOf(group.plans, 'validTo', Math.max),
    };
  });
};

/* An offer whose meal type is outside the enum cannot be asked about at all —
   the roster would 400 on it. It gets no tab, so it is named instead: a gap the
   SPOC can raise with Operations beats a plan that silently vanished. */
export const unmappedOffers = plans =>
  plans.filter(p => !p.mealType).map(p => p.name).filter(Boolean);

/* MPC-2: name the meal types that are NOT configured, so an absent tab reads as
   configuration rather than as a fault. */
export const missingMealTypes = tabs => {
  const present = new Set(tabs.map(t => t.mealType).filter(Boolean));
  return MEAL_TYPES.filter(t => !present.has(t));
};

/* MPC-19: past its validity the roster is evidence only, no longer editable. */
export const isPlanExpired = plan => {
  if (!plan?.validTo) return false;
  return moment().isAfter(fromEpoch(plan.validTo));
};

/* ---------------- Lifecycle vocabulary ----------------
   MPC-4: each state carries a distinct shape and a text label, never colour
   alone. The icon travels with the state so no caller can pick colour only. */
export const STATUS_META = {
  ACTIVE: { label: 'Active', variant: 'success', icon: 'bi-check-circle-fill' },
  PENDING: { label: 'Pending next cycle', variant: 'info', icon: 'bi-clock-fill' },
  ENDING: { label: 'Ending', variant: 'warning', icon: 'bi-hourglass-split' },
  EXCLUDED: { label: 'Not assigned', variant: 'secondary', icon: 'bi-dash-circle' },
  UNREGISTERED: {
    label: 'Not registered',
    variant: 'danger',
    icon: 'bi-exclamation-triangle-fill',
  },
};

/**
 * Derive the lifecycle state from what the roster actually reports.
 *
 * Every employee holds the plan by default; removing one writes a restriction.
 * So the roster reports `bucket` (assigned | not_assigned) alongside `status`,
 * which is one of "Active", "Restricted" or "Not registered", plus `registered`
 * and `points`.
 *
 * Restricted is split in two here. The endpoint's own note on a removal —
 * "current points stay usable; no top-up from next cycle" — is precisely the
 * product's Ending state, so someone removed while still holding a balance is
 * named Ending rather than flattened into Not assigned.
 */
export const deriveStatus = row => {
  if (!row.registered) return 'UNREGISTERED';
  if (row.bucket === 'assigned') return 'ACTIVE';
  if (row.points > 0) return 'ENDING';
  return 'EXCLUDED';
};

/**
 * The roster's own status strings, as verified against the endpoint: "Active",
 * "Restricted" and "Not registered". An unrecognised value is still rendered —
 * shown verbatim in a neutral badge rather than swallowed — so a new state
 * added server-side surfaces instead of silently reading as blank.
 */
const API_STATUS_META = {
  active: { variant: 'success', icon: 'bi-check-circle-fill' },
  restricted: { variant: 'warning', icon: 'bi-slash-circle-fill' },
  'not registered': { variant: 'danger', icon: 'bi-exclamation-triangle-fill' },
};

export const statusMetaFor = status => ({
  label: status || '—',
  ...(API_STATUS_META[String(status || '').toLowerCase()] || {
    variant: 'secondary',
    icon: 'bi-dash-circle',
  }),
});

/* MPC-4: the roster answers "has the plan or not"; the drawer names the state. */
export const isAssigned = status => status === 'ACTIVE' || status === 'PENDING';

/* The roster endpoint's own vocabulary. Sending anything outside these lists is
   a 400, so the request builder validates against them rather than trusting the
   caller. Note that effective-from is absent: the API cannot sort on it, so the
   roster does not offer that sort. */
export const SORTABLE_FIELDS = ['name', 'email', 'location', 'points'];
export const ASSIGNMENT_VALUES = ['all', 'assigned', 'not_assigned', 'not_registered'];

/** The roster page ceiling the endpoint enforces. */
export const MAX_PAGE_SIZE = 200;

/* Every plan-keyed endpoint takes `mealType` as an array of the enum's exact
   strings. Whatever the caller holds — one selection or several — is normalised
   to that, and anything outside the enum is dropped rather than sent on to be
   rejected. */
export const mealTypeList = value =>
  (Array.isArray(value) ? value : [value]).map(normalizeMealType).filter(Boolean);

/**
 * Body for /restrict and /unrestrict.
 *
 * Both take `mealType` and `walletOfferIds`, but they are not equal partners.
 * Verified against the endpoint: sending mealType ["Lunch"] together with a
 * walletOfferId belonging to Dinner came back having acted on the LUNCH offer —
 * the id was discarded. mealType resolves the offers and overrides the list.
 *
 * That makes naming a meal type unsafe the moment the SPOC has deselected one
 * of its offers: the endpoint would restrict the whole type anyway. So the meal
 * type is named only when every one of its offers is selected, and a narrowed
 * selection travels as walletOfferIds alone — which the endpoint accepts and
 * honours exactly (verified: mealType omitted, echoed back as null).
 */
export const buildMutationBody = ({
  offers = [],
  selectedOfferIds = [],
  userIds = [],
  preview = false,
}) => {
  const selected = new Set(selectedOfferIds);
  const body = {
    walletOfferIds: offers.filter(o => selected.has(o.id)).map(o => o.id),
    userIds,
    preview,
  };

  const coverage = new Map();
  offers.forEach(offer => {
    if (!offer.mealType) return;
    const entry = coverage.get(offer.mealType) || { total: 0, picked: 0 };
    entry.total += 1;
    if (selected.has(offer.id)) entry.picked += 1;
    coverage.set(offer.mealType, entry);
  });

  const whole = [...coverage.entries()]
    .filter(([, entry]) => entry.total > 0 && entry.picked === entry.total)
    .map(([type]) => type);

  const mealType = mealTypeList(whole);
  if (mealType.length) body.mealType = mealType;
  return body;
};

/* ---------------- Per-employee plan selection ----------------

   The endpoint takes ONE offer set for a whole list of users. The screen lets
   each employee have their own set, so a selection is sent as one call per
   DISTINCT set: everyone who chose the same plans travels together. In practice
   that is one or two calls, not one per person, and it degenerates to a single
   call whenever the SPOC has not varied anything. */

/** selection: { [userId]: offerId[] } -> [{ offerIds, userIds }] */
export const groupSelectionByOffers = selection => {
  const groups = new Map();

  Object.entries(selection || {}).forEach(([userId, offerIds]) => {
    const ids = [...new Set(offerIds || [])].sort();
    if (!ids.length) return; /* nothing ticked: this person is not sent at all */
    const key = ids.join('|');
    if (!groups.has(key)) groups.set(key, { offerIds: ids, userIds: [] });
    groups.get(key).userIds.push(Number(userId));
  });

  return [...groups.values()];
};

/* Several calls answer several payloads; the dialog reads one. Summaries add up
   per outcome and results concatenate — they are already unique per
   (employee x offer), so no row can collide with another group's. */
export const mergeMutationResults = payloads => {
  const summary = {};
  const results = [];

  payloads.forEach(payload => {
    Object.entries(payload?.summary || {}).forEach(([outcome, count]) => {
      summary[outcome] = (summary[outcome] || 0) + count;
    });
    if (Array.isArray(payload?.results)) results.push(...payload.results);
  });

  return { summary, results };
};

/**
 * The plans ONE employee holds in the meal type on screen, with their status
 * for each. The roster returns this per row — every offer in the queried meal
 * types, whatever the employee's state for it, so a Restricted plan is listed
 * too and can be assigned back.
 *
 * The company-wide offer list is only a fallback, for a row remembered from a
 * page that has since been replaced.
 */
export const offersForUser = (row, fallback = []) =>
  row?.offers?.length
    ? row.offers
    : fallback.map(o => ({ id: o.id, name: o.name, status: '' }));

/** Every distinct plan across the employees one dialog is about. */
export const offersAcross = (userIds, rowFor, fallback = []) => {
  const seen = new Map();
  userIds.forEach(id => {
    offersForUser(rowFor?.(id), fallback).forEach(offer => {
      if (!seen.has(offer.id)) seen.set(offer.id, { id: offer.id, name: offer.name });
    });
  });
  return [...seen.values()];
};

/**
 * Can this plan be acted on for this employee, or is the answer already known?
 *
 * Two states are certain, and both were verified against the endpoint rather
 * than assumed: assigning a plan the employee is already Active on answers
 * "no_change — not restricted", and removing one they are already Restricted
 * from answers "no_change — already restricted". Offering either as a tick box
 * is offering a click that cannot do anything.
 *
 * Every other state stays actionable and the endpoint decides — "Not
 * registered" above all. A member the roster reports as unregistered may still
 * be Restricted, and unrestricting them is perfectly valid; refusing that here
 * would be exactly the client-side guess this screen has avoided elsewhere.
 */
export const isOfferActionable = (offer, action) => {
  const status = String(offer?.status || '').trim().toLowerCase();
  if (!status) return true; /* unknown state: let the endpoint answer */
  return action === 'assign' ? status !== 'active' : status !== 'restricted';
};

/** The subset of an employee's plans this action can actually change. */
export const actionableOffers = (row, fallback, action) =>
  offersForUser(row, fallback).filter(offer => isOfferActionable(offer, action));

/**
 * Everyone starts with every plan the action can change — never one that is
 * already in the state being asked for, which would put a tick against a row
 * reading "no change" and inflate the count on the confirm button.
 */
export const defaultOfferSelection = (userIds, rowFor, fallback = [], action = 'unassign') =>
  Object.fromEntries(
    userIds.map(id => [id, actionableOffers(rowFor?.(id), fallback, action).map(o => o.id)]),
  );

/** Find one previewed outcome for a given employee-and-plan pair. */
export const resultFor = (results, userId, offerId) =>
  (results || []).find(
    r => String(r.userId) === String(userId) && r.walletOfferId === offerId,
  ) || null;

/** How many of the listed employees have this plan ticked. */
export const offerPickCount = (selection, userIds, offerId) =>
  userIds.filter(id => (selection[id] || []).includes(offerId)).length;

/**
 * Build a roster request body the schema will accept. The endpoint selects the
 * plan by `mealType`, and `walletOfferId` is no longer part of the contract —
 * sending it is a 400. It validates strictly in the other direction too, so
 * anything empty is omitted rather than sent as null, and the enumerated fields
 * are checked here instead of being trusted from the caller.
 */
export const buildRosterBody = ({
  mealType,
  page = 1,
  limit = 25,
  search = '',
  assignment = 'all',
  locationIds = [],
  sortBy = 'name',
  sortDir = 'asc',
}) => {
  const body = {
    /* The schema wants a list here — a bare string answers
       '"Meal Types" must be an array' — so the screen's single selection is
       wrapped rather than the caller being trusted to remember. */
    mealType: mealTypeList(mealType),
    page,
    limit: Math.min(limit, MAX_PAGE_SIZE),
    sortBy: SORTABLE_FIELDS.includes(sortBy) ? sortBy : 'name',
    sortDir: sortDir === 'desc' ? 'desc' : 'asc',
  };
  const term = String(search || '').trim();
  if (term) body.search = term;
  if (assignment && assignment !== 'all' && ASSIGNMENT_VALUES.includes(assignment)) {
    body.assignment = assignment;
  }
  if (Array.isArray(locationIds) && locationIds.length) {
    body.locationIds = locationIds.map(Number).filter(n => !Number.isNaN(n));
  }
  return body;
};

/* MPC-5 / MPC-6: the two buckets the roster speaks in. Clearing the control is
   what "no filter" means, so there is no All entry competing with it, and the
   endpoint's `not_registered` value stays out of the UI — those employees are
   already visible as Not registered in the Status column.
   ASSIGNMENT_VALUES above remains the full API enum: this list is what the SPOC
   is offered, not what the endpoint accepts. */
export const ASSIGNMENT_OPTIONS = [
  { value: 'assigned', label: 'Meal plan assigned' },
  { value: 'not_assigned', label: 'Meal plan not assigned' },
];

/* ---------------- Rows ---------------- */

/** One roster row, flattened for AntDTable. Sort keys mirror the API's own. */
export const normalizeRow = row => {
  const status = deriveStatus(row);
  return {
    key: row.userId,
    id: row.userId,
    name: (row.name || '').trim() || '—',
    dept: (row.department || '').trim(),
    email: row.email || '',
    location: row.location || '',
    locationId: row.locationId ?? null,
    registered: !!row.registered,
    bucket: row.bucket,
    status,
    apiStatus: (row.status || '').trim(),
    points: row.points ?? null,
    effectiveFrom: row.effectiveFrom ?? null,
    restrictedBy: row.restrictedBy || null,
    lastUpdated: row.lastUpdated ?? null,
    /* One entry per offer in the meal types that were queried, carrying this
       employee's status for each — Restricted ones included, which is what
       makes assigning one back possible. */
    offers: Array.isArray(row.plans)
      ? row.plans.map(p => ({
          id: p.walletOfferId,
          name: p.walletOfferName,
          status: p.status || '',
        }))
      : [],
    /* The plans the employee actually HOLDS, which is a shorter list: the API
       sends it as "VegLunchDev, VegMeal" and leaves the restricted ones out. */
    walletOffers: String(row.walletOffers || '')
      .split(',')
      .map(name => name.trim())
      .filter(Boolean),
  };
};

export const normalizeRows = rows => rows.map(normalizeRow);

/* Locations the current page can see, so the multi-select still offers something
   when the SPOC's own location list has not been cached (MPC-5 fallback). */
export const locationsFromRows = rows => {
  const seen = new Map();
  rows.forEach(r => {
    if (r.locationId != null && !seen.has(r.locationId)) {
      seen.set(r.locationId, { label: r.location, value: r.locationId });
    }
  });
  return [...seen.values()].sort((a, b) => a.label.localeCompare(b.label));
};

/* ---------------- Outcomes ----------------
   restrict / unrestrict answer with one result per user. These are the outcomes
   observed on the endpoint; an unknown one is shown verbatim rather than
   swallowed, because a silently dropped row is the one thing MPC-14 forbids. */
export const OUTCOME_META = {
  will_restrict: { label: 'Will be removed', tone: 'warning' },
  restricted: { label: 'Removed', tone: 'warning' },
  will_unrestrict: { label: 'Will be assigned', tone: 'success' },
  unrestricted: { label: 'Assigned', tone: 'success' },
  no_change: { label: 'No change', tone: 'secondary' },
};

export const summarise = results =>
  results.reduce((acc, r) => {
    acc[r.outcome] = (acc[r.outcome] || 0) + 1;
    return acc;
  }, {});

/** Did anything actually change? Used to choose between a success and an info toast. */
export const changedCount = results =>
  results.filter(r => r.outcome && r.outcome !== 'no_change').length;

/* A result is now one (employee x offer) pair, so the count of changes and the
   count of people are different numbers. Copy that says "3 employees" must use
   this one, or it inflates the moment a meal type carries two offers. */
export const changedUserCount = results =>
  new Set(
    results.filter(r => r.outcome && r.outcome !== 'no_change').map(r => r.userId),
  ).size;

/* Results are keyed by the pair, not by the user. */
export const resultKey = result => `${result.userId}::${result.walletOfferId || ''}`;

/* ---------------- One employee, every plan ----------------
   GET /employee/{userId}/plans answers the drawer in a single call: the person,
   a summary, and one row per wallet offer with that person's state for it. It
   replaces the old fan-out of one roster search per meal type. */

export const normalizeEmployeePlan = plan => ({
  id: plan.walletOfferId,
  name: plan.walletOfferName,
  mealType: normalizeMealType(plan.mealType),
  status: plan.status || '',
  points: plan.pointsBalance ?? null,
  effectiveFrom: plan.effectiveFrom ?? null,
  validTo: plan.planValidUntil ?? null,
  lastChangedBy: plan.lastChangedBy || null,
  lastChangedOn: plan.lastChangedOn ?? null,
});

/**
 * The drawer reads by category, because that is how the offers are organised:
 * a company can run two Lunch offers and one Dinner one, and a flat list of
 * five plan names makes the SPOC do the grouping in their head.
 *
 * So the plans come back grouped by meal type — Breakfast, Lunch, Dinner in the
 * enum's order, anything else after them in the order the API sent it — each
 * group carrying its own active count so a category with two plans can say
 * "1 of 2 active" without the panel deriving it twice.
 */
export const groupEmployeePlansByMealType = plans => {
  const groups = new Map();

  plans.forEach(plan => {
    const key = plan.mealType || 'Other';
    if (!groups.has(key)) groups.set(key, { mealType: key, plans: [] });
    groups.get(key).plans.push(plan);
  });

  const withCounts = [...groups.values()].map(group => ({
    ...group,
    active: group.plans.filter(p => String(p.status).toLowerCase() === 'active').length,
    total: group.plans.length,
  }));

  const rank = group => {
    const index = MEAL_TYPES.indexOf(group.mealType);
    return index === -1 ? MEAL_TYPES.length : index;
  };

  return withCounts.sort((a, b) => rank(a) - rank(b));
};

export const normalizeEmployeePlans = payload => {
  const plans = Array.isArray(payload?.plans)
    ? payload.plans.map(normalizeEmployeePlan)
    : [];

  return {
    employee: payload?.employee || null,
    summary: payload?.summary || null,
    plans,
    groups: groupEmployeePlansByMealType(plans),
  };
};
